import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import type { Content } from '@plone/types';
import { getContent } from '@plone/volto/actions';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { flattenToAppURL } from '@plone/volto/helpers';
import { Loader } from '@designsystem/stories/02-atoms/Loader';

interface SidepanelContentProps {
  content: Content;
  active?: boolean;
}

const initialState = {
  data: [],
  loading: false,
  loaded: false,
};

const SidepanelRender = ({ content }: { content: Content }) => {
  const location = useLocation();
  return (
    <>
      <div className="page__section page__section--inline">
        <div className="page__section--header">
          <h2 className="h1">{content?.title}</h2>
          {content?.description && (
            <p className="text--lead">{content?.description}</p>
          )}
        </div>
      </div>
      <RenderBlocks
        location={{
          ...location,
          pathname: flattenToAppURL(content['@id']),
        }}
        content={content}
      />
    </>
  );
};

const LazySidepanelContent = ({ content }: SidepanelContentProps) => {
  const dispatch = useDispatch();
  const subrequest = `sidepanel_${content.UID}`;
  const path = flattenToAppURL(content['@id']);
  const {
    data,
    loading,
    loaded,
  }: { data: Content; loading: boolean; loaded: boolean } = useSelector(
    (state: {
      content?: { subrequests?: Record<string, typeof initialState> };
    }) => state.content?.subrequests?.[subrequest] || initialState,
  ) as any;

  useEffect(() => {
    if (loaded || loading) {
      return;
    }
    dispatch(getContent(path, '', subrequest) as any);
  }, [dispatch, path, subrequest, loaded, loading]);

  if (!loaded) {
    return <Loader />;
  }

  return <SidepanelRender content={data} />;
};

const SidepanelContent = (props: SidepanelContentProps) => {
  const { active, content } = props;

  if (!active) {
    return null;
  }

  if ('blocks' in content) {
    return <SidepanelRender content={content} />;
  }

  return <LazySidepanelContent content={content} />;
};

export default SidepanelContent;
